import React from 'react';
import Icon from 'components/AppIcon';

const GameSummaryPanel = ({ selectedPlayers, stats, gameDate, playerTeams, showDetailedStats = false }) => {
  const totalPoints = selectedPlayers?.reduce((sum, p) => sum + (parseInt(stats?.[p?.id]?.points) || 0), 0);
  const totalRebounds = selectedPlayers?.reduce((sum, p) => sum + (parseInt(stats?.[p?.id]?.rebounds) || 0), 0);
  const totalAssists = selectedPlayers?.reduce((sum, p) => sum + (parseInt(stats?.[p?.id]?.assists) || 0), 0);
  const totalSteals = selectedPlayers?.reduce((sum, p) => sum + (parseInt(stats?.[p?.id]?.steals) || 0), 0);
  const totalBlocks = selectedPlayers?.reduce((sum, p) => sum + (parseInt(stats?.[p?.id]?.blocks) || 0), 0);

  const winners = selectedPlayers?.filter((p) => playerTeams?.[p?.id] === 'Winners');
  const losers = selectedPlayers?.filter((p) => playerTeams?.[p?.id] === 'Losers');
  const unassigned = selectedPlayers?.filter((p) => !playerTeams?.[p?.id]);

  const topScorer = selectedPlayers?.reduce((top, p) => {
    if (!top) return p;
    const pts = parseInt(stats?.[p?.id]?.points) || 0;
    return pts > (parseInt(stats?.[top?.id]?.points) || 0) ? p : top;
  }, null);

  const summaryStats = showDetailedStats
    ? [
        { label: 'Total Points', value: totalPoints, color: '#E85D04' },
        { label: 'Total Rebounds', value: totalRebounds, color: '#003566' },
        { label: 'Total Assists', value: totalAssists, color: '#059669' },
        { label: 'Steals + Blocks', value: totalSteals + totalBlocks, color: '#D97706' },
      ]
    : [
        { label: 'Total Points', value: totalPoints, color: '#E85D04' },
      ];

  return (
    <div className="bg-[var(--color-card)] rounded-xl border border-[var(--color-border)] p-4 md:p-5 sticky top-20">
      <h3 className="font-heading font-semibold text-base mb-4 flex items-center gap-2" style={{ color: 'var(--color-foreground)' }}>
        <Icon name="BarChart2" size={18} color="var(--color-primary)" />
        Game Summary
      </h3>

      {/* Team Breakdown */}
      <div className="space-y-2 mb-4">
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/20">
          <Icon name="Trophy" size={15} color="#059669" className="mt-0.5 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-caption text-xs font-semibold text-emerald-700 dark:text-emerald-400">
              Winners ({winners?.length})
            </p>
            {winners?.length > 0 ? (
              <p className="font-caption text-xs text-emerald-600 dark:text-emerald-500 truncate">
                {winners?.map((p) => p?.name)?.join(', ')}
              </p>
            ) : (
              <p className="font-caption text-xs text-emerald-500 italic">None assigned yet</p>
            )}
          </div>
        </div>
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/20">
          <Icon name="ThumbsDown" size={15} color="#DC2626" className="mt-0.5 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-caption text-xs font-semibold text-red-700 dark:text-red-400">
              Losers ({losers?.length})
            </p>
            {losers?.length > 0 ? (
              <p className="font-caption text-xs text-red-600 dark:text-red-500 truncate">
                {losers?.map((p) => p?.name)?.join(', ')}
              </p>
            ) : (
              <p className="font-caption text-xs text-red-500 italic">None assigned yet</p>
            )}
          </div>
        </div>
        {unassigned?.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/20">
            <Icon name="AlertCircle" size={14} color="#D97706" />
            <p className="font-caption text-xs text-amber-700 dark:text-amber-400">
              {unassigned?.length} player{unassigned?.length !== 1 ? 's' : ''} not assigned
            </p>
          </div>
        )}
      </div>

      {/* Date */}
      {gameDate && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-[var(--color-muted)]">
          <Icon name="Calendar" size={14} color="var(--color-primary)" />
          <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
            {new Date(gameDate + 'T00:00:00')?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        </div>
      )}

      {/* Players Count */}
      <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-[var(--color-muted)]">
        <Icon name="Users" size={16} color="var(--color-primary)" />
        <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)' }}>
          <span className="font-semibold">{selectedPlayers?.length}</span> player{selectedPlayers?.length !== 1 ? 's' : ''} selected
        </span>
      </div>

      {/* Stats Grid */}
      <div className={`grid gap-2 mb-4 ${showDetailedStats ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {summaryStats?.map((s) => (
          <div key={s?.label} className="p-3 rounded-lg bg-[var(--color-muted)] text-center">
            <p className="font-data font-bold text-xl" style={{ color: s?.color }}>{s?.value}</p>
            <p className="font-caption text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>{s?.label}</p>
          </div>
        ))}
      </div>

      {/* Top Scorer */}
      {topScorer && (parseInt(stats?.[topScorer?.id]?.points) || 0) > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--color-border)]">
          <Icon name="Star" size={14} color="#FFB700" />
          <div className="min-w-0">
            <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Top Scorer</p>
            <p className="font-caption font-semibold text-sm truncate" style={{ color: 'var(--color-foreground)' }}>
              {topScorer?.name} — {stats?.[topScorer?.id]?.points} pts
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameSummaryPanel;